import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ExternalLink, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import Seo, { breadcrumbJsonLd } from "@/components/Seo";

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/cases/${slug}`);
        setC(data);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#00F0FF]" />
      </div>
    );
  }

  if (notFound || !c) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
        <div className="font-display text-4xl font-black mb-4">Case study not found</div>
        <button onClick={() => navigate("/work")} className="btn-secondary mt-4">Back to Work</button>
      </div>
    );
  }

  // Schema: CreativeWork (what Google wants for case studies), plus a
  // Service node describing the practice(s) we delivered on this engagement,
  // plus — if we have quantitative results — an aggregateRating-style
  // signal via the `award` field and a Review block citing the client.
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CreativeWork",
      name: c.title,
      headline: c.title,
      description: c.tagline,
      url: `https://spinlabs.in/work/${c.slug}`,
      image: c.cover_img,
      author: { "@type": "Organization", name: "Spin Labs", url: "https://spinlabs.in" },
      creator: { "@type": "Organization", name: "Spin Labs", url: "https://spinlabs.in" },
      genre: c.category,
      dateCreated: c.year ? String(c.year) : undefined,
      about: c.services,
      ...(c.metric ? { award: c.metric } : {}),
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: (c.services || []).join(" + ") || c.category,
      provider: { "@type": "Organization", name: "Spin Labs", url: "https://spinlabs.in" },
      serviceType: c.category,
      description: c.summary || c.tagline,
      url: `https://spinlabs.in/work/${c.slug}`,
      ...(c.metric ? { award: c.metric } : {}),
    },
    // Review block — lets Google surface the outcome summary as a rich result.
    {
      "@context": "https://schema.org",
      "@type": "Review",
      itemReviewed: {
        "@type": "Organization",
        name: "Spin Labs",
        url: "https://spinlabs.in",
      },
      author: { "@type": "Organization", name: c.title },
      reviewBody: c.outcome || c.tagline,
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
      datePublished: c.year ? `${c.year}-01-01` : undefined,
    },
    breadcrumbJsonLd([
      { name: "Home", path: "/" },
      { name: "Work", path: "/work" },
      { name: c.title, path: `/work/${c.slug}` },
    ]),
  ];

  return (
    <div data-testid="case-detail-page">
      <Seo
        title={`${c.title} — Case Study`}
        path={`/work/${c.slug}`}
        description={c.tagline}
        image={c.cover_img}
        type="article"
        keywords={`${c.title}, ${c.category}, case study, ${(c.services || []).join(", ")}, Spin Labs`}
        jsonLd={jsonLd}
      />
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div
          className="spotlight w-[600px] h-[600px] top-0 left-1/3"
          style={{ background: `${c.color}22` }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <Link to="/work" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#00F0FF] mb-8">
            <ArrowLeft className="w-4 h-4" /> All case studies
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="tag-chip mb-6" style={{ borderColor: `${c.color}50`, color: c.color }}>{c.category}</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-[-0.04em] leading-[0.95] text-balance max-w-5xl">
              {c.title}
            </h1>
            <p className="mt-6 text-lg lg:text-xl text-white/70 max-w-3xl leading-relaxed">{c.tagline}</p>
            {c.external_url && (
              <a href={c.external_url} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center gap-2 text-[#00F0FF] font-bold hover:underline">
                Visit live site <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </motion.div>
        </div>
      </section>

      <section className="relative pb-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="relative aspect-[16/10] sm:aspect-[21/9] rounded-2xl sm:rounded-3xl overflow-hidden border border-white/10">
            <img src={c.cover_img} alt={c.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="scanline" />
          </div>
        </div>
      </section>

      {/* Meta strip */}
      <section className="py-8 sm:py-10 border-y border-white/5 bg-[#07070A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-5 sm:gap-6">
          <MetaItem k="Year" v={c.year} />
          <MetaItem k="Role" v={c.role} />
          <MetaItem k="Services" v={(c.services || []).join(" · ")} />
          <MetaItem k="Headline" v={c.metric} color={c.color} />
        </div>
      </section>

      {/* Content sections */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 space-y-14">
          <Block title="The challenge" body={c.challenge} />
          <Block title="Our approach" body={c.approach} />
          <Block title="The outcome" body={c.outcome} />
        </div>
      </section>

      {/* Results */}
      {c.results?.length > 0 && (
        <section className="py-16 lg:py-20 border-y border-white/5 bg-[#07070A]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="tag-chip mb-4">Results</div>
            <h2 className="font-display text-3xl lg:text-4xl font-black tracking-tighter mb-10">Numbers that moved.</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {c.results.map((r, i) => (
                <div key={i} className="p-6 border border-white/10 rounded-2xl hover:border-[#00F0FF]/40 transition-colors">
                  <div className="font-display text-4xl lg:text-5xl font-black" style={{ color: c.color }}>{r.k}</div>
                  <div className="mt-2 font-mono-neo text-xs uppercase tracking-[0.2em] text-white/60">{r.v}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      {c.gallery?.length > 1 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            {c.gallery.slice(1).map((img, i) => (
              <div key={i} className="relative aspect-video rounded-2xl overflow-hidden border border-white/10">
                <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl lg:text-5xl font-black tracking-tighter">
            Liked this? <span className="gradient-text">Let's build yours.</span>
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-primary !px-8 !py-4 !text-base group">
              Start a Project
              <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
            <Link to="/work" className="btn-secondary !px-8 !py-4 !text-base">See more work</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function MetaItem({ k, v, color }) {
  return (
    <div className="min-w-0">
      <div className="font-mono-neo text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">{k}</div>
      <div className="font-display font-bold text-sm sm:text-base lg:text-lg break-words" style={color ? { color } : undefined}>{v || "—"}</div>
    </div>
  );
}

function Block({ title, body }) {
  return (
    <div>
      <div className="font-mono-neo text-xs uppercase tracking-[0.2em] text-[#00F0FF] mb-3">{title}</div>
      <p className="text-lg text-white/80 leading-relaxed">{body}</p>
    </div>
  );
}
